import React, { useId, useState } from "react"
import AdminButton from "../../../core/admin/adminButton";
import AdminTextInput from "../../../core/admin/adminTextInput";
import AdminModal from "../../../core/admin/adminModal";
import AdminCard from "../../../core/admin/adminCard";
import AdminDetails from "../../../core/admin/adminDetails";
import AdminCallout from "../../../core/admin/adminCallout";
import AdminSwitch from "../../../core/admin/adminSwitch";
import AdminField, { AdminTextarea } from "../../../core/admin/adminField";
import AdminToast from "../../../core/admin/adminToast";
import { useAuth } from "../../../../context/authProvider";
import { adminCreateUser, AdminCreateUserResult } from "../../../../utils/adminCreateUser";
import { formatPhoneNumber, generateSecurePassword, getAuthErrorMessage, maskCPF, maskPhoneNumber, onlyDigits, PASSWORD_RULE_TEXT, validateCPF, validateEmail, validatePassword, validatePhoneNumber } from "../../../../utils/functions";
import { CheckLineIcon, FileTextIcon, PlusLineIcon, RefreshIcon } from "../../../../img/svg/adminIcons";

// mesmos limites de myContact.tsx
const MAX_FIELD = 127;
const MAX_DESCRIPTION = 235;

const emptyForm = {
    name: "",
    email: "",
    phoneNumber: "",
    password: "",
    cpf: "",
    instagram: "",
    site: "",
    description: "",
    advertiser: true
}

type Form = typeof emptyForm;

function failureMessage(result: Exclude<AdminCreateUserResult, { success: true }>) {
    switch (result.reason) {
        case "email-in-use":
            return "Já existe uma conta com este e-mail. Use a pesquisa por e-mail para configurá-la.";
        case "cpf-taken":
            return "Este CPF já está cadastrado em outra conta.";
        case "check-failed":
            return "Não foi possível confirmar se o CPF é único. Tente novamente.";
        case "auth":
            return getAuthErrorMessage(result.errorCode, "Não foi possível criar a conta. Tente novamente.");
        case "write":
            return result.rolledBack
                ? "Não foi possível salvar os dados do usuário. Nada foi criado — tente novamente."
                : "A conta foi criada, mas os dados não foram salvos por completo e não foi possível desfazê-la. Pesquise o e-mail acima para conferir o que ficou gravado.";
    }
}

export default function CreateUser() {

    const { user: actor, userId: actorId } = useAuth();
    const formId = useId();
    const ids = {
        name: useId(), email: useId(), password: useId(), phone: useId(),
        cpf: useId(), instagram: useId(), site: useId(), description: useId()
    };

    const [modal, setModal] = useState(false);
    const [form, setForm] = useState<Form>(emptyForm);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [created, setCreated] = useState<(Form & { slug: string | null }) | null>(null);
    const [copiedToast, setCopiedToast] = useState(false);

    const set = (field: keyof Form, value: string | boolean) => setForm(f => ({ ...f, [field]: value }));

    const open = () => {
        setForm(emptyForm);
        setError("");
        setCreated(null);
        setModal(true);
    }

    const close = () => {
        if (!loading) {
            setModal(false);
        }
    }

    // mesma ordem e mesmas regras do cadastro (register.tsx), mais o CPF
    const validate = () => {
        if (!form.name.trim()) {
            return "Informe o nome do usuário.";
        }
        if (!validateEmail(form.email)) {
            return "E-mail inválido. Confira o endereço digitado.";
        }
        const passwordError = validatePassword(form.password);
        if (passwordError) {
            return passwordError;
        }
        if (!validatePhoneNumber(form.phoneNumber)) {
            return "Telefone inválido. Use um celular com DDD: (xx) 9xxxx-xxxx.";
        }
        if (form.advertiser && !form.cpf) {
            return "O CPF é obrigatório para anunciantes.";
        }
        if (form.cpf && !validateCPF(form.cpf)) {
            return "CPF inválido.";
        }
        return "";
    }

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationError = validate();
        setError(validationError);
        if (validationError) {
            return;
        }
        setLoading(true);
        const result = await adminCreateUser({
            ...form,
            actor: { id: actorId, name: actor?.name || null, email: actor?.email || null }
        });
        setLoading(false);
        if (result.success) {
            setCreated({ ...form, email: form.email.trim().toLowerCase(), slug: result.slug });
        } else {
            setError(failureMessage(result));
        }
    }

    const copyAccess = async () => {
        if (!created) {
            return;
        }
        const text = [
            "Seu acesso ao GMT-3",
            `Site: ${window.location.origin}/gatekeeper`,
            `E-mail: ${created.email}`,
            `Senha: ${created.password}`,
        ].join("\n");
        try {
            await navigator.clipboard.writeText(text);
            setCopiedToast(true);
        } catch (err) {
            console.log(err);
        }
    }

    return (
        <AdminCard
            title="Criar usuário"
            description="Cadastre a conta de um vendedor conhecido já configurada e envie só o acesso pronto."
            actions={<AdminButton icon={<PlusLineIcon width="16px" />} onClick={open}>Novo usuário</AdminButton>}
        >
            <p className="text-sm text-gray-600 max-w-3xl">
                A conta nasce com e-mail e telefone marcados como verificados e, se for anunciante, com CPF e perfil público preenchidos.
                O usuário só precisa entrar com o e-mail e a senha definidos aqui e aceitar os termos de uso no primeiro acesso.
            </p>
            {
                modal ?
                    <AdminModal
                        close={close}
                        size="md"
                        title={created ? "Usuário criado" : "Novo usuário"}
                        subtitle={created ? created.email : "Todos os dados podem ser conferidos depois pela pesquisa por e-mail."}
                        footer={
                            created ?
                                <>
                                    <AdminButton variant="secondary" onClick={open}>Criar outro</AdminButton>
                                    <AdminButton onClick={close}>Fechar</AdminButton>
                                </>
                                :
                                <>
                                    <AdminButton variant="secondary" disabled={loading} onClick={close}>Cancelar</AdminButton>
                                    <AdminButton type="submit" form={formId} loading={loading} icon={<CheckLineIcon width="16px" />}>Criar usuário</AdminButton>
                                </>
                        }
                    >
                        {
                            created ?
                                <div className="flex flex-col gap-6">
                                    <AdminCallout tone="success" title="Conta pronta para uso">
                                        Envie o acesso abaixo ao usuário por um canal privado. <strong>A senha não fica salva em lugar nenhum do painel</strong> — copie antes de fechar.
                                        Se ela se perder, o usuário pode redefini-la pelo "Esqueci a senha" da tela de login.
                                    </AdminCallout>
                                    <AdminDetails
                                        items={[
                                            { label: "Nome", value: created.name.trim() },
                                            { label: "E-mail", value: created.email },
                                            { label: "Senha", value: <span className="font-mono">{created.password}</span> },
                                            { label: "Telefone", value: formatPhoneNumber(created.phoneNumber) },
                                            { label: "Papéis", value: created.advertiser ? "Cliente e anunciante" : "Cliente" },
                                            ...(created.slug ? [{ label: "Página pública", value: <span className="font-mono text-xs">/advertisers/{created.slug}</span> }] : []),
                                        ]}
                                    />
                                    <AdminButton variant="secondary" icon={<FileTextIcon width="16px" />} onClick={copyAccess} className="self-start">Copiar acesso</AdminButton>
                                </div>
                                :
                                <form id={formId} onSubmit={submit} className="flex flex-col gap-6" noValidate>
                                    <section className="flex flex-col gap-4">
                                        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Acesso</h3>
                                        <AdminField label="Nome" htmlFor={ids.name} required>
                                            <AdminTextInput id={ids.name} value={form.name} onChange={e => set("name", e.target.value)} disabled={loading} />
                                        </AdminField>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <AdminField label="E-mail" htmlFor={ids.email} required hint="Será o login do usuário. Não pode ser alterado depois.">
                                                <AdminTextInput id={ids.email} type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="usuario@email.com" disabled={loading} />
                                            </AdminField>
                                            <AdminField label="Senha" htmlFor={ids.password} required hint={PASSWORD_RULE_TEXT}>
                                                <div className="flex gap-2">
                                                    <AdminTextInput id={ids.password} value={form.password} onChange={e => set("password", e.target.value)} autoComplete="new-password" className="font-mono" disabled={loading} />
                                                    <AdminButton variant="secondary" icon={<RefreshIcon width="16px" />} onClick={() => set("password", generateSecurePassword())} disabled={loading} title="Gerar uma senha aleatória e segura">Gerar</AdminButton>
                                                </div>
                                            </AdminField>
                                        </div>
                                    </section>

                                    <section className="flex flex-col gap-4">
                                        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Contato e documento</h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <AdminField label="Telefone (celular)" htmlFor={ids.phone} required hint="Já nasce verificado e não pode ser alterado pelo usuário.">
                                                <AdminTextInput id={ids.phone} inputMode="numeric" value={maskPhoneNumber(form.phoneNumber)} onChange={e => set("phoneNumber", onlyDigits(e.target.value).substring(0, 11))} placeholder="(xx) xxxxx-xxxx" disabled={loading} />
                                            </AdminField>
                                            <AdminField label="CPF" htmlFor={ids.cpf} required={form.advertiser} hint="Não pode ser alterado depois.">
                                                <AdminTextInput id={ids.cpf} inputMode="numeric" value={form.cpf} onChange={e => set("cpf", maskCPF(e.target.value))} placeholder="000.000.000-00" disabled={loading} />
                                            </AdminField>
                                        </div>
                                    </section>

                                    <section className="flex flex-col gap-4">
                                        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Papel</h3>
                                        <AdminSwitch on={form.advertiser} onClick={() => set("advertiser", !form.advertiser)} disabled={loading} label="Tornar anunciante" />
                                        <p className="-mt-2 text-xs text-gray-500">
                                            {
                                                form.advertiser ?
                                                    "Pode cadastrar e anunciar relógios. Uma página pública de anunciante é criada."
                                                    : "Conta de cliente comum. Pode virar anunciante depois pela pesquisa por e-mail."
                                            }
                                        </p>
                                    </section>

                                    {
                                        form.advertiser ?
                                            <section className="flex flex-col gap-4">
                                                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Perfil público do anunciante <span className="normal-case tracking-normal font-semibold">(opcional)</span></h3>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                    <AdminField label="Instagram" htmlFor={ids.instagram}>
                                                        <AdminTextInput id={ids.instagram} value={form.instagram} onChange={e => set("instagram", e.target.value.substring(0, MAX_FIELD))} placeholder="@usuario" disabled={loading} />
                                                    </AdminField>
                                                    <AdminField label="Site" htmlFor={ids.site}>
                                                        <AdminTextInput id={ids.site} value={form.site} onChange={e => set("site", e.target.value.substring(0, MAX_FIELD))} placeholder="www.site.com.br" disabled={loading} />
                                                    </AdminField>
                                                </div>
                                                <AdminField label="Descrição" htmlFor={ids.description} counter={{ current: form.description.length, max: MAX_DESCRIPTION }}>
                                                    <AdminTextarea id={ids.description} value={form.description} onChange={e => set("description", e.target.value.substring(0, MAX_DESCRIPTION))} rows={3} disabled={loading} />
                                                </AdminField>
                                            </section>
                                            : null
                                    }

                                    {
                                        error ?
                                            <AdminCallout tone="danger">{error}</AdminCallout>
                                            : null
                                    }

                                    <p className="flex items-center gap-2 text-xs text-gray-500">
                                        <FileTextIcon width="14px" />
                                        <span>A criação fica registrada em <strong>Logs</strong>.</span>
                                    </p>
                                </form>
                        }
                    </AdminModal>
                    : null
            }
            <AdminToast toast={copiedToast} setToast={setCopiedToast} tone="success" message="Acesso copiado!" />
        </AdminCard>
    )
}
